// frontend/src/components/CustomerLocationCombobox.js
import { useState, useRef, useEffect, useMemo } from 'react';
import { MagnifyingGlass, X } from '@phosphor-icons/react';

const mono = { fontFamily: "'JetBrains Mono', monospace" };

function typeBadgeStyle(type) {
  if (type === 'billing')  return { color: 'hsl(142,70%,55%)', border: '1px solid hsl(142,50%,25%)', background: 'hsl(142,30%,8%)' };
  if (type === 'shipping') return { color: 'hsl(197,100%,48%)', border: '1px solid hsl(197,60%,25%)', background: 'hsl(197,30%,8%)' };
  return                          { color: 'hsl(270,60%,65%)', border: '1px solid hsl(270,40%,25%)', background: 'hsl(270,30%,8%)' };
}

/**
 * CustomerLocationCombobox
 *
 * Props:
 *   value              { customer_id, customer_name, location_id, location_nickname, location } | null
 *   onChange           (value | null) => void
 *   customers          array of customer objects (each may have .locations array from API)
 *   scopeToCustomerId  string | null — restrict results to one customer (used for ship-to picker)
 *   placeholder        string
 *   disabled           bool
 */
export default function CustomerLocationCombobox({
  value,
  onChange,
  customers = [],
  scopeToCustomerId = null,
  placeholder = 'Type customer or location name…',
  disabled = false,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Flatten customers → [{customer_id, customer_name, customer_code, location_id, location_nickname, location}]
  const allPairs = useMemo(() => {
    const scoped = scopeToCustomerId
      ? customers.filter(c => c.id === scopeToCustomerId)
      : customers;
    return scoped.flatMap(c => {
      const locs = c.locations?.length
        ? c.locations
        : [{ loc_id: 'hq', nickname: 'HQ', address: c.address || '', city: c.city || '', state: c.state || '', pincode: '', gstin: c.gstin || '', contact_name: c.contact_name || '', contact_phone: c.contact_phone || '', type: 'both' }];
      return locs.map(loc => ({
        customer_id: c.id,
        customer_name: c.name,
        customer_code: c.code || '',
        location_id: loc.loc_id,
        location_nickname: loc.nickname,
        location: loc,
      }));
    });
  }, [customers, scopeToCustomerId]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return allPairs;
    return allPairs.filter(p =>
      p.customer_name.toLowerCase().includes(q) ||
      p.customer_code.toLowerCase().includes(q) ||
      p.location_nickname.toLowerCase().includes(q) ||
      (p.location.city || '').toLowerCase().includes(q) ||
      (p.location.gstin || '').toLowerCase().includes(q)
    );
  }, [allPairs, query]);

  // Group by customer for display
  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach(p => {
      if (!map.has(p.customer_id)) map.set(p.customer_id, { customer_name: p.customer_name, pairs: [] });
      map.get(p.customer_id).pairs.push(p);
    });
    return [...map.values()];
  }, [filtered]);

  function select(pair) {
    onChange({
      customer_id: pair.customer_id,
      customer_name: pair.customer_name,
      location_id: pair.location_id,
      location_nickname: pair.location_nickname,
      location: pair.location,
    });
    setQuery('');
    setOpen(false);
  }

  function clear(e) {
    e.stopPropagation();
    onChange(null);
    setQuery('');
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function handleKeyDown(e) {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown')  { e.preventDefault(); setHighlightIdx(i => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlightIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter')  { e.preventDefault(); if (filtered[highlightIdx]) select(filtered[highlightIdx]); }
    else if (e.key === 'Escape') setOpen(false);
  }

  useEffect(() => { setHighlightIdx(0); }, [query]);

  useEffect(() => {
    function onMouseDown(e) {
      if (!listRef.current?.contains(e.target) && !inputRef.current?.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const inputStyle = {
    background: 'hsl(0,0%,6%)',
    border: `1px solid ${open ? 'hsl(197,100%,48%)' : 'hsl(0,0%,20%)'}`,
    color: '#fff',
    padding: '8px 12px 8px 28px',
    width: '100%',
    boxSizing: 'border-box',
    ...mono,
    fontSize: 12,
    outline: 'none',
  };

  // ── Selected state ──────────────────────────────────────────────────────────
  if (value) {
    const tc = typeBadgeStyle(value.location?.type);
    return (
      <div>
        <div style={{ border: '1px solid hsl(197,100%,30%)', background: 'hsl(197,20%,6%)', padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            {!scopeToCustomerId && (
              <>
                <span style={{ ...mono, fontSize: 10, color: 'hsl(0,0%,50%)' }}>{value.customer_name}</span>
                <span style={{ color: 'hsl(0,0%,35%)', margin: '0 6px', fontSize: 10 }}>→</span>
              </>
            )}
            <span style={{ ...mono, fontSize: 12, color: 'hsl(197,100%,70%)', fontWeight: 600 }}>{value.location_nickname}</span>
          </div>
          {!disabled && (
            <button onClick={clear} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(0,0%,45%)', padding: '0 0 0 8px', display: 'flex', alignItems: 'center' }}>
              <X size={12} />
            </button>
          )}
        </div>
        <div style={{ background: 'hsl(0,0%,4%)', border: '1px solid hsl(0,0%,12%)', borderTop: 'none', padding: '4px 12px', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          {value.location?.gstin && <span style={{ ...mono, fontSize: 10, color: 'hsl(0,0%,45%)' }}>GSTIN: <span style={{ color: 'hsl(0,0%,60%)' }}>{value.location.gstin}</span></span>}
          {value.location?.city && <span style={{ ...mono, fontSize: 10, color: 'hsl(0,0%,40%)' }}>{value.location.city}{value.location.state ? `, ${value.location.state}` : ''}</span>}
          {value.location?.type && <span style={{ ...mono, fontSize: 8, padding: '1px 6px', ...tc }}>{value.location.type.toUpperCase()}</span>}
        </div>
      </div>
    );
  }

  // ── Search / empty state ────────────────────────────────────────────────────
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <MagnifyingGlass size={12} color="hsl(0,0%,40%)" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
        <input
          ref={inputRef}
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          style={inputStyle}
          data-testid="customer-location-combobox-input"
          autoComplete="off"
        />
      </div>
      {open && (
        <div ref={listRef} style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,16%)', borderTop: 'none', maxHeight: 280, overflowY: 'auto' }}>
          {filtered.length === 0 ? (
            <div style={{ ...mono, fontSize: 11, color: 'hsl(0,0%,40%)', padding: '12px 16px' }}>
              {query ? `No results for "${query}"` : 'No locations available'}
            </div>
          ) : grouped.map(group => (
            <div key={group.customer_name}>
              {!scopeToCustomerId && (
                <div style={{ ...mono, fontSize: 9, color: 'hsl(0,0%,35%)', padding: '6px 12px 2px', textTransform: 'uppercase', letterSpacing: '0.1em', borderTop: '1px solid hsl(0,0%,9%)' }}>
                  {group.customer_name}
                </div>
              )}
              {group.pairs.map(pair => {
                const globalIdx = filtered.indexOf(pair);
                const isHighlighted = globalIdx === highlightIdx;
                const tc = typeBadgeStyle(pair.location.type);
                return (
                  <div
                    key={`${pair.customer_id}-${pair.location_id}`}
                    onMouseDown={() => select(pair)}
                    style={{ padding: '8px 12px', cursor: 'pointer', background: isHighlighted ? 'hsl(0,0%,9%)' : 'transparent', borderLeft: isHighlighted ? '2px solid hsl(197,100%,48%)' : '2px solid transparent' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <span style={{ ...mono, fontSize: 11, color: 'hsl(0,0%,88%)', fontWeight: 600 }}>{pair.location_nickname}</span>
                        {pair.location.city && (
                          <span style={{ ...mono, fontSize: 10, color: 'hsl(0,0%,45%)', marginLeft: 8 }}>
                            {pair.location.city}{pair.location.state ? `, ${pair.location.state}` : ''}
                          </span>
                        )}
                      </div>
                      {pair.location.type && (
                        <span style={{ ...mono, fontSize: 8, padding: '1px 5px', ...tc }}>
                          {pair.location.type.toUpperCase()}
                        </span>
                      )}
                    </div>
                    {pair.location.gstin && (
                      <div style={{ ...mono, fontSize: 9, color: 'hsl(0,0%,40%)', marginTop: 2 }}>GSTIN: {pair.location.gstin}</div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
